<template>
  <div class="standards-page">
    <header class="brand-header">
      <div class="header-container">
        <router-link to="/" class="brand-info" aria-label="SaveAnimals home">
          <img src="/img/logo.png" alt="SaveAnimals logo" class="brand-logo" />
          <div>
            <p class="brand-title">SaveAnimals</p>
            <p class="brand-tagline">Wildlife Rescue Tracking System</p>
          </div>
        </router-link>
        <div class="partner-logos" aria-label="Developer and conservation partners">
          <img src="/img/wti_logo.png" alt="Wildlife Trust of India" class="partner-logo" />
          <img src="/img/leopardlogo.png" alt="Leopard Tech Labs Pvt. Ltd." class="partner-logo" />
        </div>
      </div>
    </header>

    <main class="page-main">
      <div class="policy-layout">
        <aside class="policy-sidebar" aria-label="Page sections">
          <nav class="sidebar-sticky">
            <p class="sidebar-eyebrow">On this page</p>
            <a v-for="section in sections" :key="section.id" :href="'#' + section.id"
              :class="{ active: activeSection === section.id }" @click.prevent="scrollTo(section.id)">
              {{ section.title }}
            </a>
          </nav>
        </aside>

        <article class="policy-article">
          <header class="document-header">
            <p class="document-kicker">Public standards</p>
            <h1>Child Safety Standards</h1>
            <div class="meta-row">
              <span><strong>Effective:</strong> July 16, 2026</span>
              <span><strong>Last updated:</strong> July 16, 2026</span>
              <span><strong>Version:</strong> 1.0</span>
            </div>
            <p class="lead">
              These standards apply to the <strong>SaveAnimals mobile app and web platform</strong>, developed and published by <strong>Leopard Tech Labs Pvt. Ltd.</strong> in partnership with the Wildlife Trust of India (WTI) and the International Fund for Animal Welfare (IFAW).
            </p>
          </header>

          <section id="commitment" class="commitment-card">
            <div class="commitment-mark" aria-hidden="true">!</div>
            <div>
              <p class="commitment-label">Our commitment</p>
              <h2>Zero tolerance for CSAE and CSAM</h2>
              <p>SaveAnimals explicitly prohibits Child Sexual Abuse and Exploitation (CSAE) and Child Sexual Abuse Material (CSAM). Our platform must never be used to harm, exploit, sexualize, groom, or endanger a child.</p>
            </div>
          </section>

          <section id="prohibited-conduct" class="policy-section">
            <p class="section-number">01</p>
            <h2>Prohibited conduct</h2>
            <p>No person may use SaveAnimals to create, upload, store, share, request, promote, facilitate, or distribute CSAE or CSAM. This prohibition includes:</p>
            <ul class="standard-list">
              <li>Sexualizing a minor or depicting, describing, encouraging, or normalizing the sexual abuse or exploitation of a child.</li>
              <li>Grooming, predatory conduct, or building trust with a child for sexual purposes.</li>
              <li>Sextortion, including threats or coercion involving sexual content or intimate imagery of a minor.</li>
              <li>Child trafficking, solicitation, enticement, or facilitating sexual contact with a minor.</li>
              <li>Requesting, offering, linking to, or exchanging CSAM, including altered or computer-generated material where prohibited by law.</li>
              <li>Attempts to move CSAE-related conversations, exchanges, or conduct from SaveAnimals to another service or offline setting.</li>
            </ul>
            <p>Attempts, instructions, coded requests, or other conduct intended to evade these standards are also prohibited.</p>
          </section>

          <section id="reporting" class="policy-section reporting-section">
            <p class="section-number">02</p>
            <h2>Report a child-safety concern</h2>
            <div class="danger-note">
              <strong>If a child is in immediate danger, contact local emergency services or law enforcement first.</strong> SaveAnimals is not an emergency response service.
            </div>
            <p>For a child-safety issue involving SaveAnimals, contact our designated point of contact:</p>
            <div class="contact-panel">
              <p class="contact-role">Child Safety Team — Leopard Tech Labs Pvt. Ltd.</p>
              <a class="report-button" href="mailto:info@leopardtechlabs.com?subject=SaveAnimals%20Child%20Safety%20Report">Email the Child Safety Team</a>
              <p class="contact-address">Email: <strong>info@leopardtechlabs.com</strong></p>
            </div>
            <p>Include enough detail to help us locate and review the issue, such as the relevant account, case, date, URL, or other identifier. <strong>Do not attach, copy, download, or redistribute suspected CSAM.</strong> Sharing such material can cause further harm and may be unlawful.</p>
          </section>

          <section id="review-enforcement" class="policy-section">
            <p class="section-number">03</p>
            <h2>Review and enforcement</h2>
            <p>Reports and detected violations are reviewed promptly by authorized personnel. Depending on the circumstances and applicable law, SaveAnimals may:</p>
            <ul class="standard-list">
              <li>Remove, disable, or restrict access to prohibited content.</li>
              <li>Suspend or permanently terminate accounts and access to SaveAnimals.</li>
              <li>Preserve relevant account information and evidence where lawful.</li>
              <li>Cooperate with law enforcement and qualified child-safety organizations.</li>
              <li>Report confirmed CSAM to the National Center for Missing &amp; Exploited Children (NCMEC), or the appropriate regional authority, when required by applicable law.</li>
            </ul>
            <p>Enforcement decisions prioritize child safety, legal obligations, the integrity of an investigation, and prevention of further harm.</p>
          </section>

          <section id="legal-compliance" class="policy-section">
            <p class="section-number">04</p>
            <h2>Legal compliance and cooperation</h2>
            <p>SaveAnimals and Leopard Tech Labs Pvt. Ltd. comply with applicable child-safety laws and reporting requirements in the jurisdictions where the service operates. We may respond to valid legal requests and provide information to competent authorities as permitted or required by law.</p>
            <p>These standards supplement our <router-link to="/privacy-policy">Privacy Policy</router-link>, which explains how personal information is collected, used, retained, and disclosed.</p>
          </section>

          <section id="scope" class="policy-section final-section">
            <p class="section-number">05</p>
            <h2>Scope and updates</h2>
            <p>These standards apply to all content, communications, accounts, and activity on the SaveAnimals mobile app and web platform. Leopard Tech Labs Pvt. Ltd. may update these standards to reflect legal, operational, or platform-policy requirements. The revision date above identifies the current version.</p>
          </section>
        </article>
      </div>
    </main>

    <footer class="policy-footer">
      <div class="footer-container">
        <div>
          <strong>SaveAnimals</strong>
          <p>Developed and published by Leopard Tech Labs Pvt. Ltd. in partnership with WTI and IFAW.</p>
        </div>
        <nav aria-label="Policy links">
          <router-link to="/privacy-policy">Privacy Policy</router-link>
          <a href="#commitment" @click.prevent="scrollTo('commitment')">Back to top</a>
          <router-link to="/login">Portal Login</router-link>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ChildSafetyStandards',
  data() {
    return {
      activeSection: 'commitment',
      sections: [
        { id: 'commitment', title: 'Zero-tolerance commitment' },
        { id: 'prohibited-conduct', title: 'Prohibited conduct' },
        { id: 'reporting', title: 'Report a concern' },
        { id: 'review-enforcement', title: 'Review & enforcement' },
        { id: 'legal-compliance', title: 'Legal compliance' },
        { id: 'scope', title: 'Scope & updates' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    scrollTo(id) {
      const element = document.getElementById(id)
      this.activeSection = id
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    handleScroll() {
      const marker = window.scrollY + 140
      this.sections.forEach(section => {
        const element = document.getElementById(section.id)
        if (element && marker >= element.offsetTop && marker < element.offsetTop + element.offsetHeight) {
          this.activeSection = section.id
        }
      })
    }
  }
}
</script>

<style scoped>
.standards-page {
  --forest: #1b4332;
  --forest-dark: #081c15;
  --green: #2d6a4f;
  --mint: #d8f3dc;
  --gold: #d4a72c;
  --ink: #263b32;
  --muted: #5c6b64;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: var(--ink);
  background: #f7fbf9;
  min-height: 100vh;
  line-height: 1.7;
}
.brand-header { background: linear-gradient(135deg, var(--forest) 0%, var(--forest-dark) 100%); border-bottom: 4px solid #52b788; padding: 1.35rem 2rem; color: white; box-shadow: 0 4px 20px rgba(0,0,0,.15); }
.header-container, .footer-container { max-width: 1200px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; }
.brand-info { display: flex; align-items: center; gap: 1rem; color: white; text-decoration: none; }
.brand-logo { width: 60px; height: 60px; object-fit: contain; background: white; padding: 4px; border-radius: 8px; }
.brand-title { margin: 0; font-size: 1.8rem; line-height: 1.15; font-weight: 800; letter-spacing: -.03em; }
.brand-tagline { margin: .2rem 0 0; color: #95d5b2; font-size: .9rem; }
.partner-logos { display: flex; align-items: center; gap: 1rem; }
.partner-logo { height: 40px; max-width: 130px; object-fit: contain; background: white; padding: 3px 8px; border-radius: 6px; }
.page-main { padding: 2.5rem 1rem 4rem; background-image: radial-gradient(circle at 85% 10%, rgba(82,183,136,.1), transparent 25rem); }
.policy-layout { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 260px minmax(0, 1fr); gap: 2.5rem; align-items: start; }
.sidebar-sticky { position: sticky; top: 2rem; background: white; padding: 1.4rem; border: 1px solid #e1e9e5; border-radius: 12px; box-shadow: 0 4px 15px rgba(27,67,50,.04); }
.sidebar-eyebrow { color: var(--forest); text-transform: uppercase; letter-spacing: .12em; font-size: .72rem; font-weight: 800; padding-bottom: .7rem; margin: 0 0 .55rem; border-bottom: 2px solid var(--mint); }
.sidebar-sticky a { display: block; color: var(--muted); text-decoration: none; border-left: 3px solid transparent; padding: .55rem .65rem; font-size: .88rem; line-height: 1.35; transition: .2s ease; }
.sidebar-sticky a:hover, .sidebar-sticky a.active { color: var(--forest); background: #edf8f0; border-left-color: #52b788; }
.policy-article { background: white; border: 1px solid #e2ece7; border-radius: 16px; padding: 2.75rem; box-shadow: 0 8px 30px rgba(27,67,50,.06); }
.document-header { border-bottom: 1px solid #e8f0ec; padding-bottom: 2rem; margin-bottom: 2rem; }
.document-kicker, .commitment-label, .section-number { margin: 0 0 .45rem; color: var(--green); font-size: .75rem; letter-spacing: .14em; text-transform: uppercase; font-weight: 800; }
h1 { color: var(--forest); font-size: clamp(2.2rem, 5vw, 3.4rem); line-height: 1.08; letter-spacing: -.045em; margin: 0 0 .85rem; }
.meta-row { display: flex; flex-wrap: wrap; gap: .5rem 1.5rem; font-size: .86rem; color: #64766e; margin-bottom: 1.35rem; }
.meta-row strong { color: var(--green); }
.lead { font-size: 1.08rem; color: #43584f; margin: 0; max-width: 52rem; }
.commitment-card { display: grid; grid-template-columns: 58px 1fr; gap: 1.25rem; padding: 2rem; color: white; background: linear-gradient(135deg, #244f3d, var(--forest-dark)); border-left: 5px solid var(--gold); border-radius: 10px; box-shadow: 0 12px 28px rgba(8,28,21,.15); scroll-margin-top: 2rem; }
.commitment-mark { display: grid; place-items: center; width: 52px; height: 52px; border: 2px solid #f3d477; color: #f3d477; font-size: 1.55rem; font-weight: 900; border-radius: 50%; }
.commitment-label { color: #f3d477; }
.commitment-card h2 { color: white; font-size: 1.65rem; margin: 0 0 .5rem; }
.commitment-card p:last-child { color: #d8f3dc; margin: 0; }
.policy-section { position: relative; padding: 2.7rem 0 .2rem; scroll-margin-top: 2rem; }
.policy-section + .policy-section { border-top: 1px solid #e8f0ec; }
.policy-section h2 { color: var(--forest); font-size: 1.45rem; line-height: 1.3; margin: 0 0 1rem; }
.section-number { color: #8b6a10; }
.standard-list { list-style: none; padding: 0; margin: 1.2rem 0; }
.standard-list li { position: relative; padding: .68rem .75rem .68rem 2rem; border-bottom: 1px solid #edf2ef; }
.standard-list li::before { content: '\2713'; position: absolute; left: .35rem; color: var(--green); font-weight: 900; }
.danger-note { padding: 1rem 1.15rem; margin: 0 0 1.3rem; color: #653113; background: #fff8e7; border: 1px solid #eed58c; border-left: 4px solid var(--gold); }
.contact-panel { padding: 1.5rem; margin: 1rem 0 1.3rem; background: #edf8f0; border: 1px solid #cbe8d4; }
.contact-role { color: var(--forest); font-weight: 800; margin: 0 0 .25rem; }
.report-button { display: block; width: fit-content; margin-top: 1rem; padding: .65rem 1rem; background: var(--forest); color: white; text-decoration: none; border: 2px solid var(--forest); font-weight: 700; border-radius: 5px; transition: .2s ease; }
.report-button:hover, .report-button:focus { color: var(--forest); background: white; }
.contact-address { margin: .75rem 0 0; color: #52675d; font-size: .9rem; overflow-wrap: anywhere; }
.policy-section a { color: #1e6949; font-weight: 700; text-decoration: underline; text-underline-offset: 3px; }
.final-section { padding-bottom: 0; }
.policy-footer { padding: 2rem; background: var(--forest-dark); border-top: 4px solid var(--gold); color: #b7d4c5; }
.policy-footer p { margin: .2rem 0 0; font-size: .86rem; }
.policy-footer nav { display: flex; gap: 1.25rem; flex-wrap: wrap; }
.policy-footer a { color: white; text-decoration: none; border-bottom: 1px solid #7aa88f; }
a:focus-visible { outline: 3px solid #f3d477; outline-offset: 3px; }
@media (max-width: 850px) {
  .policy-layout { grid-template-columns: 1fr; }
  .policy-sidebar { display: none; }
  .policy-article { padding: 2rem; }
}
@media (max-width: 600px) {
  .brand-header { padding: 1rem; }
  .header-container { flex-direction: column; align-items: stretch; gap: .75rem; }
  .brand-info { min-width: 0; }
  .partner-logos { width: auto; padding-left: 76px; flex-wrap: wrap; gap: .65rem; }
  .partner-logo { height: 32px; max-width: 105px; }
  .brand-title { font-size: 1.45rem; }
  .page-main { padding: 1rem .65rem 2rem; }
  .policy-article { padding: 1.35rem; border-radius: 10px; }
  h1 { font-size: 2.15rem; }
  .commitment-card { grid-template-columns: 1fr; padding: 1.4rem; }
  .footer-container { align-items: flex-start; flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { scroll-behavior: auto !important; transition: none !important; }
}
</style>
